import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Youtube, Users, MessageSquare, ArrowLeft, Save, X, ExternalLink } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
  };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("episodes");
  const [episodes, setEpisodes] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [guestApps, setGuestApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEp, setEditingEp] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", youtube_id: "", duration: "", episode_number: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const authFetch = useCallback(async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: getHeaders() });
    if (res.status === 401) {
      localStorage.removeItem("admin_token");
      navigate("/admin");
      return null;
    }
    return res;
  }, [navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [epRes, subRes, gaRes] = await Promise.all([
        authFetch(`${API_URL}/api/admin/episodes`),
        authFetch(`${API_URL}/api/admin/subscribers`),
        authFetch(`${API_URL}/api/admin/guest-applications`),
      ]);
      if (epRes) setEpisodes(await epRes.json());
      if (subRes) setSubscribers(await subRes.json());
      if (gaRes) setGuestApps(await gaRes.json());
    } catch {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/admin");
      return;
    }
    loadData();
  }, [navigate, loadData]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const openNewForm = () => {
    const nextNum = episodes.length > 0 ? Math.max(...episodes.map(e => e.episode_number)) + 1 : 1;
    setForm({ title: "", description: "", youtube_id: "", duration: "", episode_number: String(nextNum) });
    setEditingEp(null);
    setShowForm(true);
    setError("");
  };

  const openEditForm = (ep) => {
    setForm({
      title: ep.title,
      description: ep.description,
      youtube_id: ep.youtube_id,
      duration: ep.duration || "",
      episode_number: String(ep.episode_number),
    });
    setEditingEp(ep);
    setShowForm(true);
    setError("");
  };

  const extractYouTubeId = (input) => {
    if (!input) return "";
    // Already a plain ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(input.trim())) return input.trim();
    // URL patterns
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const p of patterns) {
      const m = input.match(p);
      if (m) return m[1];
    }
    return input.trim();
  };

  const saveEpisode = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const ytId = extractYouTubeId(form.youtube_id);
    const body = {
      title: form.title.trim(),
      description: form.description.trim(),
      youtube_id: ytId,
      duration: form.duration.trim(),
      episode_number: parseInt(form.episode_number),
    };

    try {
      let res;
      if (editingEp) {
        res = await authFetch(`${API_URL}/api/admin/episodes/${editingEp.id}`, { method: "PUT", body: JSON.stringify(body) });
      } else {
        res = await authFetch(`${API_URL}/api/admin/episodes`, { method: "POST", body: JSON.stringify(body) });
      }
      if (res && res.ok) {
        setShowForm(false);
        await loadData();
      } else if (res) {
        const data = await res.json();
        setError(typeof data.detail === "string" ? data.detail : "Failed to save");
      }
    } catch {
      setError("Connection failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteEpisode = async (ep) => {
    if (!window.confirm(`Delete Ep. ${ep.episode_number}: "${ep.title}"?`)) return;
    try {
      const res = await authFetch(`${API_URL}/api/admin/episodes/${ep.id}`, { method: "DELETE" });
      if (res && res.ok) await loadData();
    } catch {
      setError("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="text-[#5C5A56]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="admin-dashboard">
      {/* Top Bar */}
      <header className="bg-white border-b border-[#E6E4DF] px-4 md:px-8 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <a href="/" className="text-[#5C5A56] hover:text-[#D36135] transition-colors" data-testid="admin-back-link">
            <ArrowLeft size={18} />
          </a>
          <span className="logo-text text-base text-[#2D2C2A]">
            TCC <span className="text-[#D36135]">Admin</span>
          </span>
        </div>
        <button onClick={logout} className="flex items-center gap-1.5 text-sm text-[#5C5A56] hover:text-red-500 transition-colors" data-testid="admin-logout-button">
          <LogOut size={16} /> Logout
        </button>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 border border-[#E6E4DF] text-center">
            <Youtube size={20} className="text-[#D36135] mx-auto mb-1" />
            <p className="text-2xl font-bold text-[#2D2C2A]">{episodes.length}</p>
            <p className="text-xs text-[#5C5A56]">Episodes</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-[#E6E4DF] text-center">
            <Users size={20} className="text-[#D36135] mx-auto mb-1" />
            <p className="text-2xl font-bold text-[#2D2C2A]">{subscribers.length}</p>
            <p className="text-xs text-[#5C5A56]">Subscribers</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-[#E6E4DF] text-center">
            <MessageSquare size={20} className="text-[#D36135] mx-auto mb-1" />
            <p className="text-2xl font-bold text-[#2D2C2A]">{guestApps.length}</p>
            <p className="text-xs text-[#5C5A56]">Guest Apps</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-[#E6E4DF] mb-6">
          {[
            { key: "episodes", label: "Episodes", icon: Youtube },
            { key: "subscribers", label: "Subscribers", icon: Users },
            { key: "guests", label: "Guest Apps", icon: MessageSquare },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === key ? "bg-[#2D2C2A] text-white" : "text-[#5C5A56] hover:bg-[#F9F8F6]"
              }`}
              data-testid={`tab-${key}`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {/* Episodes Tab */}
        {activeTab === "episodes" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#2D2C2A]">Manage Episodes</h2>
              <button
                onClick={openNewForm}
                className="flex items-center gap-1.5 bg-[#D36135] hover:bg-[#B34E27] text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors"
                data-testid="add-episode-button"
              >
                <Plus size={16} /> Add Episode
              </button>
            </div>

            {/* Episode Form Modal */}
            {showForm && (
              <div className="bg-white rounded-xl border border-[#E6E4DF] p-5 mb-4 shadow-[0_4px_20px_rgba(45,44,42,0.06)]" data-testid="episode-form">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[#2D2C2A]">{editingEp ? "Edit Episode" : "New Episode"}</h3>
                  <button onClick={() => setShowForm(false)} className="text-[#5C5A56] hover:text-red-500"><X size={18} /></button>
                </div>
                <form onSubmit={saveEpisode} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#5C5A56] mb-1">Episode #</label>
                      <input
                        type="number" min="1" required value={form.episode_number}
                        onChange={(e) => setForm({ ...form, episode_number: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-[#E6E4DF] bg-[#F9F8F6] text-sm focus:outline-none focus:border-[#D36135]"
                        data-testid="episode-number-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#5C5A56] mb-1">Duration</label>
                      <input
                        type="text" placeholder="e.g. 48:00" value={form.duration}
                        onChange={(e) => setForm({ ...form, duration: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-[#E6E4DF] bg-[#F9F8F6] text-sm focus:outline-none focus:border-[#D36135]"
                        data-testid="episode-duration-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#5C5A56] mb-1">Title</label>
                    <input
                      type="text" required value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-[#E6E4DF] bg-[#F9F8F6] text-sm focus:outline-none focus:border-[#D36135]"
                      placeholder="Episode title"
                      data-testid="episode-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#5C5A56] mb-1">YouTube Link or ID</label>
                    <input
                      type="text" required value={form.youtube_id}
                      onChange={(e) => setForm({ ...form, youtube_id: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-[#E6E4DF] bg-[#F9F8F6] text-sm focus:outline-none focus:border-[#D36135]"
                      placeholder="Paste YouTube URL or video ID"
                      data-testid="episode-youtube-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#5C5A56] mb-1">Description</label>
                    <textarea
                      required value={form.description} rows={3}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-[#E6E4DF] bg-[#F9F8F6] text-sm focus:outline-none focus:border-[#D36135] resize-none"
                      placeholder="Brief episode description"
                      data-testid="episode-description-input"
                    />
                  </div>
                  {error && <p className="text-red-500 text-xs" data-testid="episode-form-error">{error}</p>}
                  <button
                    type="submit" disabled={saving}
                    className="flex items-center gap-1.5 bg-[#2D2C2A] hover:bg-[#D36135] text-white text-sm font-medium px-5 py-2 rounded-xl transition-colors disabled:opacity-50"
                    data-testid="save-episode-button"
                  >
                    <Save size={15} /> {saving ? "Saving..." : editingEp ? "Update Episode" : "Add Episode"}
                  </button>
                </form>
              </div>
            )}

            {/* Episodes List */}
            <div className="space-y-2">
              {episodes.map((ep) => (
                <div key={ep.id} className="bg-white rounded-xl border border-[#E6E4DF] p-3 flex items-center gap-3 group hover:border-[#D36135]/30 transition-colors" data-testid={`admin-episode-${ep.episode_number}`}>
                  <img
                    src={`https://img.youtube.com/vi/${ep.youtube_id}/mqdefault.jpg`}
                    alt=""
                    className="w-28 h-16 object-cover rounded-lg flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="bg-[#D36135] text-white text-[10px] px-2 py-0.5 rounded-full font-medium">Ep. {ep.episode_number}</span>
                      {ep.duration && <span className="text-[10px] text-[#5C5A56]">{ep.duration}</span>}
                    </div>
                    <p className="text-sm font-medium text-[#2D2C2A] truncate">{ep.title}</p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <a
                      href={`https://www.youtube.com/watch?v=${ep.youtube_id}`}
                      target="_blank" rel="noopener noreferrer"
                      className="p-2 text-[#5C5A56] hover:text-[#D36135] transition-colors"
                      data-testid={`view-episode-${ep.episode_number}`}
                    >
                      <ExternalLink size={15} />
                    </a>
                    <button onClick={() => openEditForm(ep)} className="p-2 text-[#5C5A56] hover:text-[#D36135] transition-colors" data-testid={`edit-episode-${ep.episode_number}`}>
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => deleteEpisode(ep)} className="p-2 text-[#5C5A56] hover:text-red-500 transition-colors" data-testid={`delete-episode-${ep.episode_number}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
              {episodes.length === 0 && (
                <p className="text-center text-sm text-[#5C5A56] py-8">No episodes yet. Add your first one!</p>
              )}
            </div>
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === "subscribers" && (
          <div>
            <h2 className="text-lg font-bold text-[#2D2C2A] mb-4">Newsletter Subscribers</h2>
            {subscribers.length === 0 ? (
              <p className="text-center text-sm text-[#5C5A56] py-8">No subscribers yet.</p>
            ) : (
              <div className="bg-white rounded-xl border border-[#E6E4DF] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9F8F6] border-b border-[#E6E4DF]">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-medium text-[#5C5A56]">Email</th>
                      <th className="text-left px-4 py-2.5 font-medium text-[#5C5A56]">Date</th>
                      <th className="text-left px-4 py-2.5 font-medium text-[#5C5A56]">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub, i) => (
                      <tr key={sub.id || i} className="border-b border-[#E6E4DF] last:border-0" data-testid={`subscriber-row-${i}`}>
                        <td className="px-4 py-2.5 text-[#2D2C2A]">{sub.email}</td>
                        <td className="px-4 py-2.5 text-[#5C5A56]">{sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : "-"}</td>
                        <td className="px-4 py-2.5 text-[#5C5A56]">{sub.source || "website"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Guest Apps Tab */}
        {activeTab === "guests" && (
          <div>
            <h2 className="text-lg font-bold text-[#2D2C2A] mb-4">Guest Applications</h2>
            {guestApps.length === 0 ? (
              <p className="text-center text-sm text-[#5C5A56] py-8">No guest applications yet.</p>
            ) : (
              <div className="space-y-2">
                {guestApps.map((app, i) => (
                  <div key={app.id || i} className="bg-white rounded-xl border border-[#E6E4DF] p-4" data-testid={`guest-app-${i}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-[#2D2C2A]">{app.name}</p>
                        <p className="text-xs text-[#5C5A56]">{app.email}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        app.status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"
                      }`}>{app.status}</span>
                    </div>
                    <p className="text-sm text-[#2D2C2A] mb-1"><strong>Topic:</strong> {app.topic}</p>
                    {app.about && <p className="text-xs text-[#5C5A56]">{app.about}</p>}
                    {app.social && <p className="text-xs text-[#D36135] mt-1">{app.social}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
